/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps, import/no-anonymous-default-export, no-useless-escape */
import React,{useState,useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import Layout from '../components/layout/Layout';
import {BarChart,Bar,XAxis,YAxis,CartesianGrid,Tooltip,ResponsiveContainer,AreaChart,Area,PieChart,Pie,Cell,LineChart,Line} from 'recharts';
import api from '../services/api';
import {fmtCurrency} from '../utils/helpers';
import {downloadCSV} from '../utils/exportUtils';
import {getCurrencySymbol} from '../utils/locale';
import {useTranslation} from 'react-i18next';

const COLORS=['#2563eb','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899'];

export default function ReportsPage(){
  const {t}=useTranslation();
  const nav=useNavigate();
  const [tab,setTab]=useState<'overview'|'revenue'|'opd'|'ipd'|'pharmacy'|'lab'>('overview');
  const [dateRange,setDateRange]=useState({from:new Date(Date.now()-30*86400000).toISOString().split('T')[0],to:new Date().toISOString().split('T')[0]});
  const [summary,setSummary]=useState<any>({});
  const [revenueData,setRevenueData]=useState<any[]>([]);
  const [patientData,setPatientData]=useState<any[]>([]);
  const [opdData,setOpdData]=useState<any[]>([]);
  const [loading,setLoading]=useState(false);

  useEffect(()=>{
    api.getDashboardSummary().then(r=>setSummary(r.data||{})).catch(()=>{});
    loadReports();
  },[]);

  const loadReports=async()=>{
    setLoading(true);
    try{
      const[rev,pat,opd]=await Promise.all([
        api.getRevenueReport({from:dateRange.from,to:dateRange.to}),
        api.getBillingStats?.().catch(()=>({data:{}})),
        api.getPatientReport({from:dateRange.from,to:dateRange.to}),
        api.getOPDReport({from:dateRange.from,to:dateRange.to}),
      ]);
      setRevenueData(rev.data||[]);
      setPatientData(pat.data||[]);
      setOpdData(opd.data||[]);
    }catch{}finally{setLoading(false);}
  };

  // Exports whatever tabular data is currently loaded for the active tab. Falls back to the
  // overview summary stats when a tab has no row-level data yet (e.g. ipd/pharmacy/lab, which
  // are still placeholder tabs). No new dependency — reuses the CSV helper built for AuditPage.
  const exportExcel=()=>{
    if(tab==='revenue'&&revenueData.length){
      downloadCSV(revenueData,`revenue-report-${dateRange.from}-to-${dateRange.to}`,[
        {key:'date',label:'Date'},{key:'bill_count',label:'Bills'},{key:'total_billed',label:'Billed'},
        {key:'total_collected',label:'Collected'},{key:'total_pending',label:'Pending'},
      ]);
      return;
    }
    const rows=[
      {metric:'Total Patients',value:summary.patients?.total||0},
      {metric:"Today's OPD",value:summary.opd?.today||0},
      {metric:'Active IPD',value:summary.ipd?.admitted||0},
      {metric:'Month Revenue',value:summary.billing?.month_collection||0},
    ];
    downloadCSV(rows,`reports-overview-${dateRange.from}-to-${dateRange.to}`,[{key:'metric',label:'Metric'},{key:'value',label:'Value'}]);
  };
  const printPDF=()=>window.print();

  const REPORT_CARDS=[
    {label:'Patient Registration',icon:'👥',desc:'Daily registrations, demographics, gender distribution',color:'blue'},
    {label:'Revenue Analysis',icon:'💰',desc:'Collection, pending, payment methods breakdown',color:'green'},
    {label:'OPD Statistics',icon:'🏥',desc:'Consultations, doctor-wise analysis, diagnosis trends',color:'purple'},
    {label:'IPD Report',icon:'🛏️',desc:'Admissions, discharges, occupancy, length of stay',color:'amber'},
    {label:'Pharmacy Sales',icon:'💊',desc:'Drug sales, revenue, top drugs, expiry report',color:'teal'},
    {label:'Laboratory Report',icon:'🔬',desc:'Tests performed, turnaround time, critical values',color:'red'},
    {label:'Radiology Report',icon:'🩻',desc:'Orders, modality-wise stats, pending reports',color:'indigo'},
    {label:'Blood Bank Report',icon:'🩸',desc:'Stock levels, requests, transfusions',color:'pink'},
    {label:'Doctor Performance',icon:'👨‍⚕️',desc:'Consultations per doctor, revenue generated',color:'cyan'},
    {label:'HRMS Report',icon:'👔',desc:'Attendance, leaves, payroll summary',color:'orange'},
    {label:'Insurance/TPA',icon:'📋',desc:'Claims submitted, approved, pending',color:'slate'},
    {label:'MIS Report',icon:'📊',desc:'Management information system daily summary',color:'blue'},
    {label:'Audit Trail',icon:'🔍',desc:'Full activity log — who did what, when',color:'slate',to:'/audit'},
    {label:'Login History',icon:'🔐',desc:'Login/logout events, failed attempts, devices',color:'indigo',to:'/audit'},
    {label:'Security Alerts',icon:'🚨',desc:'Auto-flagged high-risk and suspicious activity',color:'red',to:'/audit'},
  ];

  const deptDist=[
    {name:'General',value:32,color:'#2563eb'},{name:'Cardiology',value:18,color:'#10b981'},
    {name:'Ortho',value:14,color:'#f59e0b'},{name:'Gynae',value:16,color:'#ef4444'},
    {name:'ENT',value:12,color:'#8b5cf6'},{name:'Others',value:8,color:'#ec4899'},
  ];

  const mockRevenue=[
    {date:'01 Jan',collected:42000,billed:52000},{date:'08 Jan',collected:38000,billed:45000},
    {date:'15 Jan',collected:55000,billed:62000},{date:'22 Jan',collected:48000,billed:56000},
    {date:'29 Jan',collected:61000,billed:70000},{date:'05 Feb',collected:58000,billed:66000},
  ];

  return(
    <Layout title="Reports & Analytics">
      <div className="flex gap-2 mb-5 flex-wrap">
        {[['overview','📊 Overview'],['revenue','💰 Revenue'],['opd','🏥 OPD'],['ipd','🛏️ IPD'],['pharmacy','💊 Pharmacy'],['lab','🔬 Lab']].map(([t,l])=>(
          <button key={t} onClick={()=>setTab(t as any)} className={`px-4 py-2 rounded-xl text-xs font-semibold whitespace-nowrap border-2 flex-shrink-0 transition-all ${tab===t?'bg-[#02357A] text-white border-[#02357A] shadow-lg shadow-[#02357A]/20':'bg-white text-slate-600 border-slate-200 hover:border-[#02357A] hover:text-[#02357A]'}`}>{l}</button>
        ))}
      </div>

      {/* Date Range Filter */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-5 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2"><label className="text-sm font-medium text-slate-600">From:</label><input type="date" value={dateRange.from} onChange={e=>setDateRange({...dateRange,from:e.target.value})} className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400 w-40 text-sm"/></div>
        <div className="flex items-center gap-2"><label className="text-sm font-medium text-slate-600">To:</label><input type="date" value={dateRange.to} onChange={e=>setDateRange({...dateRange,to:e.target.value})} className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400 w-40 text-sm"/></div>
        <button onClick={loadReports} disabled={loading} className="bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold py-2.5 px-5 rounded-xl transition-all text-sm shadow-md shadow-[#02357A]/20 disabled:opacity-50 disabled:cursor-not-allowed">{loading?'Loading...':'Generate Report'}</button>
        <div className="flex gap-2 ml-auto">
          <button onClick={exportExcel} className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2.5 px-5 rounded-xl border-2 border-slate-200 hover:border-slate-300 transition-all text-sm disabled:opacity-50">📥 Export Excel</button>
          <button onClick={printPDF} className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2.5 px-5 rounded-xl border-2 border-slate-200 hover:border-slate-300 transition-all text-sm disabled:opacity-50">🖨️ Print PDF</button>
        </div>
      </div>

      {tab==='overview'&&(
        <div className="space-y-5">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {l:'Total Patients',v:summary.patients?.total||1247,c:'blue',i:'👥'},
              {l:"Today's OPD",v:summary.opd?.today||34,c:'green',i:'🏥'},
              {l:'Active IPD',v:summary.ipd?.admitted||38,c:'amber',i:'🛏️'},
              {l:'Month Revenue',v:fmtCurrency(summary.billing?.month_collection||1820000),c:'purple',i:'💰'},
            ].map(s=>(
              <div key={s.l} className={`card p-5 flex items-center gap-3`}>
                <span className="text-3xl">{s.i}</span>
                <div><p className="text-slate-500 text-xs">{s.l}</p><p className={`text-2xl font-bold text-${s.c}-600`}>{s.v}</p></div>
              </div>
            ))}
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 card p-5">
              <h3 className="font-semibold text-slate-700 mb-4">Revenue Trend (Billed vs Collected)</h3>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={mockRevenue}>
                  <defs>
                    <linearGradient id="gc" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#2563eb" stopOpacity={0.15}/><stop offset="95%" stopColor="#2563eb" stopOpacity={0}/></linearGradient>
                    <linearGradient id="gb" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10b981" stopOpacity={0.15}/><stop offset="95%" stopColor="#10b981" stopOpacity={0}/></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                  <XAxis dataKey="date" tick={{fontSize:11}} axisLine={false} tickLine={false}/>
                  <YAxis tick={{fontSize:11}} axisLine={false} tickLine={false} tickFormatter={v=>`${getCurrencySymbol()}${(v/1000).toFixed(0)}K`}/>
                  <Tooltip formatter={(v:any)=>fmtCurrency(v)}/>
                  <Area type="monotone" dataKey="billed" stroke="#2563eb" strokeWidth={2} fill="url(#gc)" name="Billed"/>
                  <Area type="monotone" dataKey="collected" stroke="#10b981" strokeWidth={2} fill="url(#gb)" name="Collected"/>
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
              <h3 className="font-semibold text-slate-700 mb-4">Patient Distribution</h3>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart><Pie data={deptDist} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value" paddingAngle={3}>
                  {deptDist.map((d,i)=><Cell key={i} fill={d.color}/>)}
                </Pie><Tooltip/></PieChart>
              </ResponsiveContainer>
              <div className="space-y-1 mt-2">
                {deptDist.map(d=>(
                  <div key={d.name} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{background:d.color}}/><span className="text-slate-600">{d.name}</span></div>
                    <span className="font-semibold text-slate-700">{d.value}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Report Cards */}
          <div>
            <h3 className="font-semibold text-slate-700 mb-4">Available Reports</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {REPORT_CARDS.map(r=>(
                <div key={r.label} className={`card p-4 hover:shadow-md transition-all cursor-pointer border-t-4 border-${r.color}-400`}>
                  <div className="flex items-center gap-2 mb-2"><span className="text-2xl">{r.icon}</span><h4 className="font-semibold text-slate-700 text-sm">{r.label}</h4></div>
                  <p className="text-slate-500 text-xs mb-3">{r.desc}</p>
                  <div className="flex gap-2">
                    <button onClick={()=>r.to?nav(r.to):setTab('overview')} className="bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold py-2.5 px-5 rounded-xl transition-all text-sm shadow-md shadow-[#02357A]/20 disabled:opacity-50 disabled:cursor-not-allowed text-xs py-1 px-2.5 flex-1">{r.to?'View →':'Generate'}</button>
                    <button className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2.5 px-5 rounded-xl border-2 border-slate-200 hover:border-slate-300 transition-all text-sm disabled:opacity-50 text-xs py-1 px-2">📅</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab==='revenue'&&(
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          <h3 className="font-semibold text-slate-700 mb-4">Revenue Report ({dateRange.from} to {dateRange.to})</h3>
          {revenueData.length>0?(
            <div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={revenueData.slice(0,14)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                  <XAxis dataKey="date" tick={{fontSize:11}} axisLine={false} tickLine={false}/>
                  <YAxis tick={{fontSize:11}} axisLine={false} tickLine={false} tickFormatter={v=>`${getCurrencySymbol()}${(+v/1000).toFixed(0)}K`}/>
                  <Tooltip formatter={(v:any)=>fmtCurrency(+v)}/>
                  <Bar dataKey="total_billed" name="Billed" fill="#2563eb" radius={[4,4,0,0]}/>
                  <Bar dataKey="total_collected" name="Collected" fill="#10b981" radius={[4,4,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-5 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50"><tr><th className="px-4 py-2 text-left text-xs text-slate-500">{t('common.date','Date')}</th><th className="px-4 py-2 text-right text-xs text-slate-500">Bills</th><th className="px-4 py-2 text-right text-xs text-slate-500">Billed</th><th className="px-4 py-2 text-right text-xs text-slate-500">Collected</th><th className="px-4 py-2 text-right text-xs text-slate-500">{t('common.pending','Pending')}</th></tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {revenueData.map((r:any,i)=>(
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="px-4 py-2 font-medium">{r.date}</td>
                        <td className="px-4 py-2 text-right">{r.bill_count||0}</td>
                        <td className="px-4 py-2 text-right text-[#02357A] font-medium">{fmtCurrency(+r.total_billed||0)}</td>
                        <td className="px-4 py-2 text-right text-green-600 font-medium">{fmtCurrency(+r.total_collected||0)}</td>
                        <td className="px-4 py-2 text-right text-red-500">{fmtCurrency(+r.total_pending||0)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-50 font-semibold">
                    <tr>
                      <td className="px-4 py-2">{t('common.total','Total')}</td>
                      <td className="px-4 py-2 text-right">{revenueData.reduce((s:number,r:any)=>s+(+r.bill_count||0),0)}</td>
                      <td className="px-4 py-2 text-right text-[#02357A]">{fmtCurrency(revenueData.reduce((s:number,r:any)=>s+(+r.total_billed||0),0))}</td>
                      <td className="px-4 py-2 text-right text-green-600">{fmtCurrency(revenueData.reduce((s:number,r:any)=>s+(+r.total_collected||0),0))}</td>
                      <td className="px-4 py-2 text-right text-red-500">{fmtCurrency(revenueData.reduce((s:number,r:any)=>s+(+r.total_pending||0),0))}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          ):<div className="py-12 text-center"><span className="text-5xl block mb-3">💰</span><p className="text-slate-500">Click "Generate Report" to load data</p></div>}
        </div>
      )}

      {['opd','ipd','pharmacy','lab'].includes(tab)&&(
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
          <span className="text-5xl block mb-3">📊</span>
          <p className="text-slate-500 mb-2 font-medium capitalize">{tab.toUpperCase()} Report</p>
          <p className="text-slate-400 text-sm mb-4">Date range: {dateRange.from} to {dateRange.to}</p>
          <button onClick={loadReports} className="bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold py-2.5 px-5 rounded-xl transition-all text-sm shadow-md shadow-[#02357A]/20 disabled:opacity-50 disabled:cursor-not-allowed">Generate {tab.toUpperCase()} Report</button>
        </div>
      )}
    </Layout>
  );
}
